// packages/web/src/components/teach/AddToHomeworkModal.tsx
import React, { useEffect, useState } from 'react';
import { useExerciseLibraryStore } from '../../stores/exerciseLibraryStore';
import { homeworkApi } from '../../services/homeworkApi';
import { X } from 'lucide-react';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

export const AddToHomeworkModal: React.FC = () => {
  const { exercises, setShowAddToHomeworkModal, deselectAllExercises } = useExerciseLibraryStore();
  const [homeworks, setHomeworks] = useState<any[]>([]);
  const [selectedHomeworkId, setSelectedHomeworkId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast, success, error, warning, hideToast } = useToast();

  const selectedExercises = exercises.filter(ex => ex.selected);

  useEffect(() => {
    fetchHomeworks();
  }, []);

  const fetchHomeworks = async () => {
    setLoading(true);
    try {
      const response = await homeworkApi.getList();
      // 只显示活跃的作业
      const activeHomeworks = response.data.homeworks.filter(hw => hw.status === 'active');
      setHomeworks(activeHomeworks);
    } catch (error) {
      console.error('Failed to fetch homeworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedHomeworkId) {
      warning('请选择作业');
      return;
    }

    // 检查科目一致性
    const selectedHomework = homeworks.find(hw => hw.id === selectedHomeworkId);
    if (!selectedHomework) return;

    const exerciseCategories = new Set(selectedExercises.map(ex => ex.category));
    if (exerciseCategories.size > 1) {
      warning('所选习题包含不同科目，无法添加到同一作业');
      return;
    }

    const exerciseCategory = Array.from(exerciseCategories)[0];
    if (exerciseCategory !== selectedHomework.subject) {
      warning(`习题科目（${exerciseCategory}）与作业科目（${selectedHomework.subject}）不一致`);
      return;
    }

    setSubmitting(true);
    try {
      await homeworkApi.addExercises(
        selectedHomeworkId,
        selectedExercises.map(ex => ex.id)
      );
      
      success('添加成功');
      deselectAllExercises();
      setTimeout(() => setShowAddToHomeworkModal(false), 1000);
    } catch (err: any) {
      error(err.response?.data?.error || '添加失败');
    } finally {
      setSubmitting(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 背景遮罩 */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={() => setShowAddToHomeworkModal(false)}
        />

        {/* 模态窗口 */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* 头部 */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">加入作业</h3>
            <button
              onClick={() => setShowAddToHomeworkModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="px-6 py-4">
            {/* 已选习题列表 */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                已选习题 ({selectedExercises.length})
              </h4>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {selectedExercises.map((exercise) => (
                  <div key={exercise.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-900 truncate flex-1">{exercise.title}</span>
                    <span className="text-xs text-gray-500 ml-2">
                      {getCategoryLabel(exercise.category)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 选择作业 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                选择作业
              </label>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : homeworks.length === 0 ? (
                <p className="text-sm text-gray-500">暂无可用作业，请先创建作业</p>
              ) : (
                <select
                  value={selectedHomeworkId}
                  onChange={(e) => setSelectedHomeworkId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">请选择作业</option>
                  {homeworks.map((homework) => (
                    <option key={homework.id} value={homework.id}>
                      {homework.name} - {getCategoryLabel(homework.subject)} - 截止: {new Date(homework.due_date).toLocaleDateString('zh-CN')}
                    </option>
                  ))}
                </select>
              )}

              {selectedHomeworkId && (
                <p className="mt-2 text-xs text-gray-500">
                  提示：习题将被添加到所选作业中，如果作业已分配给学生，系统会自动为学生创建新的题目副本
                </p>
              )}
            </div>
          </div>

          {/* 底部 */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowAddToHomeworkModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={submitting}
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedHomeworkId || submitting || homeworks.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? '添加中...' : '确认添加'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
